/* eslint-disable sonarjs/cognitive-complexity */
import { useMutation } from '@tanstack/react-query';
import { add, format, isSameDay, parseISO } from 'date-fns';
import { isEmpty, omit } from 'lodash';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
  memo,
} from 'react';
import { Spinner } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Stack from 'react-bootstrap/Stack';
import { Controller, FieldArrayWithId, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { BsTrash } from 'react-icons/bs';
import { toast } from 'react-toastify';
import {
  BookingRequestsValidateService,
  ProviderBookingValidate,
  ProviderRateTypeEnum,
} from '../../../../api';
import { parseTime } from '../../../../utils/time';
import { DATE_FORMAT } from '../../../constants/time';
import { FormSubmitContext } from '../../../contexts/FormSubmitContext';
import { ProviderLimitContext } from '../../../contexts/ProviderLimitContext';
import { useDebounce } from '../../../hooks/debounce';
import { useCurrencyFormatter } from '../../../hooks/use-currency-formatter';
import { AddShiftModal } from '../AddShiftModal';
import {
  getAvailablePresets,
  getInitialShifts,
  setToFormEndDateDueToSelectedTime,
  updatePreset,
} from '../Helper/ShiftSelectorHelper';
import { FieldsAction, ProviderScheduleWithKey } from '../Scheduler.types';
import {
  FormOutput,
  IndividualPresetType,
  IndividualShiftPresets,
  IShiftPreset,
  Shift,
  SHIFT_BOOTSTRAP_COL_NAME,
  ShiftFieldType,
  ShiftPreset,
  SHIFTS_PRESET_MAP,
} from '../ShiftPreset';
import './HCOShiftSelectorModal.scss';

interface Props {
  isBooking: boolean;
  facilityId: string | null;
  providerId: string;
  isLoading: boolean;
  selectedDates: Date[];
  schedules?: ProviderScheduleWithKey[];
  schedulesForAdding?: ProviderScheduleWithKey[];
  onConfirm: (shiftByDate: FormOutput) => void;
  setSelectedDates: Dispatch<SetStateAction<Date[]>>;
  onOpenConfirmPopup: () => void;
  shifts: Shift[];
}

export const HCOShiftSelectorModal = memo(
  ({
    isBooking,
    isLoading,
    facilityId,
    providerId,
    selectedDates,
    schedules = [],
    schedulesForAdding = [],
    onConfirm,
    setSelectedDates,
    onOpenConfirmPopup,
    shifts,
  }: Props) => {
    const { isSubmitEnabled } = useContext(FormSubmitContext);
    const { providerLimit } = useContext(ProviderLimitContext);
    const { formatCurrency } = useCurrencyFormatter({ placeholder: '-' });

    const [selectedDatesWithShifts, setSelectedDatesWithShifts] = useState<Date[]>(selectedDates);
    const [presets, setPresets] = useState<IShiftPreset[]>([]);
    const [openAddShiftModal, setOpenAddShiftModal] = useState<boolean>(false);
    const [customSelectedTimes, setCustomSelectedTimes] = useState({});
    const [individualPresetType, setIndividualPresetType] = useState<IndividualPresetType>({});
    const validationInProgress = useRef(false);
    const loaderRef = useRef(false);

    const form = useForm<FormOutput>({
      defaultValues: { shifts, comment: '' },
    });
    const formErrors = form.formState.errors;
    const { fields: shiftsFields, remove: removeSchedule } = useFieldArray({
      name: 'shifts',
      control: form.control,
    });

    const formFields = useWatch({ control: form.control, name: 'shifts' });

    const { mutateAsync: bookingValidate, isLoading: isValidateLoading } = useMutation({
      mutationFn: async (data: { shifts: Shift[] }) => {
        form.clearErrors();

        const healthcare_facility_id = facilityId as string;
        const provider_id = providerId;
        const schedules = data.shifts.map((shift) => {
          const startOn = parseISO(shift.startOn);
          const nextDay = add(startOn, { days: 1 });

          const endOn =
            shift.startTime >= shift.endTime
              ? format(nextDay, DATE_FORMAT)
              : format(startOn, DATE_FORMAT);

          return {
            shift_type: shift.presetType,
            end_on: endOn,
            start_on: shift.startOn,
            start_time: shift.startTime,
            end_time: shift.endTime,
          };
        });

        validationInProgress.current = true;
        const { comment } = form.getValues();

        return BookingRequestsValidateService.bookingRequestsValidatePartialUpdate({
          requestBody: {
            schedules,
            healthcare_facility_id,
            provider_id,
            comment,
          },
        });
      },
      onError: (errors) => {
        console.error(errors);
        validationInProgress.current = false;
      },
      onSuccess: (validatedShifts) => {
        const response = validatedShifts as any as ProviderBookingValidate[]; // looks like a bug in the API response

        const updatedFields = formFields.map((field, index) => {
          const validatedShift = response.find((item) => item.id === field.id);

          if (!validatedShift?.valid) {
            form.setError(`shifts.${index}.startTime`, {
              type: 'manual',
              message: validatedShift?.error as string,
            });
            form.setError(`shifts.${index}.endTime`, {
              type: 'manual',
              message: validatedShift?.error as string,
            });

            return {
              ...field,
              totalRatesValue: validatedShift?.total_value || '0',
            };
          }

          if (validatedShift) {
            return {
              ...field,
              startOn: validatedShift.start_on,
              endOn: validatedShift.end_on,
              presetType: validatedShift.shift_type,
              providerRate: validatedShift.provider_rate,
              totalRatesValue: validatedShift.total_value,
            };
          }
          return field;
        });

        form.setValue('shifts', updatedFields);
        setPresets(getAvailablePresets(providerLimit, SHIFTS_PRESET_MAP, response));

        // This is a workaround to prevent multiple validations in a short period of time
        setTimeout(() => {
          validationInProgress.current = false;
        }, 200);
      },
    });

    const updateBookingValidate = useDebounce(bookingValidate, 350);

    useEffect(() => {
      const shiftFieldsMap = shiftsFields.reduce((acc, elem) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        acc[elem.key] = elem.presetType;
        return acc;
      }, {});
      setIndividualPresetType(shiftFieldsMap);
    }, [shiftsFields]);

    useEffect(() => {
      if (validationInProgress.current) {
        return;
      }

      if (formFields?.length) {
        updateBookingValidate({ shifts: formFields });
      }
    }, [formFields, updateBookingValidate]);

    useEffect(() => {
      if (shifts.length) {
        form.reset();
        form.setValue('shifts', shifts);
      }
    }, [shifts, form]);

    const updateFields = useCallback(
      (dates: Date[], action: FieldsAction) => {
        loaderRef.current = true;
        let updatesDates: Date[] = [];
        setSelectedDates((prevState) => {
          updatesDates =
            action === FieldsAction.Add
              ? [...dates, ...prevState]
              : prevState.filter(
                  (date) => format(date, DATE_FORMAT) !== format(dates[0], DATE_FORMAT)
                );
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          updatesDates.sort((a, b) => a - b);
          return updatesDates;
        });
        setSelectedDatesWithShifts(updatesDates);
        const updatesFields = getInitialShifts(updatesDates, schedules);
        form.reset();
        form.setValue('shifts', updatesFields);
      },
      [form, schedules, setSelectedDates]
    );

    const handleRemoveShift = async (index: number) => {
      const hasOtherShiftsOnDate = !!shiftsFields.find(
        (schedule, i) => i !== index && schedule.startOn === shiftsFields[index].startOn
      );
      if (!hasOtherShiftsOnDate && selectedDatesWithShifts?.length === 1) {
        toast.error('Cannot remove all shifts. Please cancel instead.');
        return;
      }
      removeSchedule(index);
      if (!hasOtherShiftsOnDate) {
        setSelectedDatesWithShifts(
          selectedDatesWithShifts.filter(
            (date) => format(date, DATE_FORMAT) !== shiftsFields[index].startOn
          )
        );
      }
      updateFields([new Date(shiftsFields[index].startOn)], FieldsAction.Remove);
    };

    const applyPreset = (preset: IShiftPreset): void => {
      shiftsFields.forEach((schedule, index) => {
        updatePreset({ index, preset, shiftField: schedule, setValue: form.setValue });
      });
      setCustomSelectedTimes({});
      form.clearErrors();
    };

    const handleCustomTimeChange =
      (
        index: number,
        schedule: FieldArrayWithId<FormOutput, 'shifts', 'id'>,
        field: 'startTime' | 'endTime'
      ) =>
      (e): void => {
        setCustomSelectedTimes((prev) => ({
          ...prev,
          [index]: {
            ...schedule,
            ...prev[index],
            [field]: e.target.value,
          },
        }));

        const startTime = field === 'startTime' ? e.target.value : schedule.startTime;
        const endTime = field === 'endTime' ? e.target.value : schedule.endTime;

        setToFormEndDateDueToSelectedTime({
          startTime,
          endTime,
          index,
          scheduleDate: parseISO(schedule.startOn),
          form,
        });
      };

    const handleApplyToAll = (index) => {
      const { startTime, endTime, presetType } = customSelectedTimes[index];
      shiftsFields.forEach((schedule, index) => {
        form.setValue(`shifts.${index}.startTime`, startTime);
        form.setValue(`shifts.${index}.endTime`, endTime);
        form.setValue(`shifts.${index}.presetType`, presetType);

        setToFormEndDateDueToSelectedTime({
          startTime,
          endTime,
          index,
          scheduleDate: parseISO(schedule.startOn),
          form,
        });
      });

      setCustomSelectedTimes((prev) => omit(prev[index]));
      form.clearErrors();
    };

    const handleChangeIndividualPreset = useCallback(
      (scheduleId: string, shiftType: ProviderRateTypeEnum) => {
        setIndividualPresetType((prevState) => {
          return {
            ...prevState,
            [scheduleId]: shiftType,
          };
        });
      },
      []
    );

    const applyIndividualPreset = (preset: IShiftPreset, shiftField: ShiftFieldType): void => {
      const index: number = shiftsFields.findIndex((shift) => shift.id === shiftField.id);
      updatePreset({ index, preset, shiftField, setValue: form.setValue });
      setCustomSelectedTimes({});
      form.clearErrors();
    };

    const addNewShift = () => {
      setOpenAddShiftModal(true);
    };

    const colSize = useMemo(() => SHIFT_BOOTSTRAP_COL_NAME(presets.length), [presets]);
    const totalPayment = useMemo(() => {
      return shiftsFields.reduce((acc, shiftField) => {
        return acc + (shiftField?.totalRatesValue ? +shiftField.totalRatesValue : 0);
      }, 0);
    }, [shiftsFields]);

    useEffect(() => {
      if (!isValidateLoading && loaderRef.current) {
        loaderRef.current = false;
      }
    }, [isValidateLoading]);

    const handleSubmit = async (shiftByDate: FormOutput) => {
      const response = await bookingValidate({ shifts: formFields });
      const validateResponse = response as any as ProviderBookingValidate[];
      const isInValid = validateResponse?.some((item) => !item.valid);

      if (!isEmpty(form.formState.errors) || isInValid) return;

      onConfirm(shiftByDate);
    };

    return (
      <Form
        noValidate
        onSubmit={form.handleSubmit(handleSubmit)}
        className="hoc-shift-request-form"
      >
        <Modal.Header closeButton>
          <Stack direction="vertical" gap={2}>
            <Modal.Title>{isBooking ? 'Schedule Shifts' : 'Select Availability'}</Modal.Title>
          </Stack>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: '75vh' }}>
          {presets.length > 1 && (
            <div className="p-3">
              <h5 className="mb-3">
                Apply predefined time to all shifts or customize each shift separately
              </h5>
              <Row className="preset-list">
                {presets.map((preset, index) => (
                  <ShiftPreset
                    key={index}
                    preset={preset}
                    index={index}
                    colSize={colSize}
                    onApplyPreset={applyPreset}
                  />
                ))}
              </Row>
            </div>
          )}
          <Stack className="schedules-list p-3">
            <div className="d-flex justify-content-between">
              <h5>Shifts</h5>
              <Button variant="tertiary" size="sm" onClick={addNewShift} className="add-new-shift">
                + Add shift
              </Button>
              <AddShiftModal
                onClose={() => setOpenAddShiftModal(false)}
                show={openAddShiftModal}
                schedules={schedulesForAdding}
                updateFields={updateFields}
              />
            </div>
            {loaderRef.current ? (
              <Row>
                <Col xs={12} className="d-flex align-items-center justify-content-center">
                  <Spinner
                    aria-label="loading indicator"
                    animation="grow"
                    className="loading-spinner"
                    variant="primary"
                  />
                </Col>
              </Row>
            ) : (
              shiftsFields.map((schedule, index) => (
                <div
                  className="schedules-list-item d-flex align-items-start custom-pb-pt-12 border-bottom"
                  key={schedule.id}
                >
                  <div>{SHIFTS_PRESET_MAP[schedule.presetType]?.icon}</div>

                  <div className="w-100 d-flex flex-column gap-custom-12">
                    <div className="pe-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center shift-date flex-wrap">
                          <Controller
                            control={form.control}
                            name={`shifts.${index}.startOn`}
                            render={({ field: { value } }) => (
                              <span>{parseISO(value).toDateString()}</span>
                            )}
                          />
                          {!isSameDay(
                            new Date(shiftsFields[index].startOn),
                            new Date(shiftsFields[index].endOn)
                          ) && (
                            <>
                              <span className="ms-1 me-1">-</span>
                              <Controller
                                control={form.control}
                                name={`shifts.${index}.endOn`}
                                render={({ field: { value } }) => (
                                  <span>{parseISO(value).toDateString()}</span>
                                )}
                              />
                            </>
                          )}
                        </div>
                        <div className="shift-rate">
                          {formatCurrency(shiftsFields[index].totalRatesValue)}
                        </div>
                      </div>
                      <Stack className="d-flex justify-content-between w-100 shift-provider-availability">
                        Provider availability:{' '}
                        {format(parseTime(shiftsFields[index].startTime), 'HH:mm')} –{' '}
                        {format(parseTime(shiftsFields[index].endTime), 'HH:mm')}
                      </Stack>
                    </div>
                    <div className="d-flex justify-content-between w-100">
                      <div className="d-flex align-items-center">
                        <Stack
                          className="justify-content-start align-items-center wrapped"
                          direction="horizontal"
                          gap={2}
                        >
                          <Form.Group className="position-relative max-width-180">
                            <Form.Control
                              type="time"
                              className="time-input"
                              disabled={isLoading}
                              isInvalid={!!formErrors?.shifts?.[index]?.startTime?.message}
                              {...form.register(`shifts.${index}.startTime`, {
                                required: 'Start time is required',
                                onChange: handleCustomTimeChange(index, schedule, 'startTime'),
                              })}
                            />
                            <Form.Control.Feedback tooltip type="invalid">
                              {formErrors?.shifts?.[index]?.startTime?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <Form.Group className="position-relative max-width-180">
                            <Form.Control
                              type="time"
                              className="time-input"
                              disabled={isLoading}
                              isInvalid={!!formErrors?.shifts?.[index]?.endTime?.message}
                              {...form.register(`shifts.${index}.endTime`, {
                                required: 'End time is required',
                                onChange: handleCustomTimeChange(index, schedule, 'endTime'),
                              })}
                            />
                            <Form.Control.Feedback tooltip type="invalid">
                              {formErrors?.shifts?.[index]?.endTime?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <div className="d-flex flex-nowrap">
                            <IndividualShiftPresets
                              onChangeIndividualPreset={handleChangeIndividualPreset}
                              currentType={individualPresetType[schedule.key]}
                              shiftField={schedule}
                              presets={presets}
                              colSize={colSize}
                              onApplyPreset={applyIndividualPreset}
                            />
                            {!!customSelectedTimes[index] && shiftsFields?.length > 1 && (
                              <Button
                                onClick={() => handleApplyToAll(index)}
                                variant="link"
                                className="apply-all-btn"
                              >
                                Apply to All
                              </Button>
                            )}
                          </div>
                        </Stack>
                      </div>
                      {shiftsFields.length > 1 && (
                        <div className="d-flex justify-content-end" style={{ minWidth: 48 }}>
                          <Button
                            variant="link"
                            className="text-danger"
                            size="lg"
                            onClick={() => handleRemoveShift(index)}
                            title="delete"
                          >
                            <BsTrash />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </Stack>
          {isBooking && (
            <Form.Group className="p-3">
              <Form.Control
                type="text"
                as="textarea"
                placeholder="Enter comment"
                disabled={isLoading}
                isInvalid={!!formErrors?.comment}
                {...form.register('comment')}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div className="d-flex justify-content-between width-100">
            <div className="d-flex justify-content-between">
              <span>Total payment:</span>
              <span className="shifts-total-payment">{formatCurrency(totalPayment)}</span>
            </div>
            <p className="desktop hidden mb-0">
              {shiftsFields.length} Shifts / {selectedDatesWithShifts.length} Dates
            </p>
          </div>
          <div className="d-flex align-items-center gap-2 coll-reverse">
            <p className="mobile hidden mb-0">
              {shiftsFields.length} Shifts / {selectedDatesWithShifts.length} Dates
            </p>
            <Button
              className="btn"
              variant="outline-primary"
              disabled={isLoading}
              onClick={onOpenConfirmPopup}
            >
              Cancel
            </Button>
            <Button
              className="btn"
              variant="primary"
              type="submit"
              disabled={isLoading || !isSubmitEnabled}
            >
              {isBooking ? 'Send Request' : 'Adjust Availability'}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    );
  },
  (oldProps, newProps) => {
    return (
      oldProps.facilityId !== newProps.facilityId ||
      oldProps.providerId !== newProps.providerId ||
      oldProps.selectedDates !== newProps.selectedDates ||
      oldProps.schedules !== newProps.schedules ||
      oldProps.schedulesForAdding !== newProps.schedulesForAdding ||
      oldProps.shifts !== newProps.shifts ||
      oldProps.isLoading !== newProps.isLoading
    );
  }
);

HCOShiftSelectorModal.displayName = 'HCOShiftSelectorModal';
